/**
 * The stand-in Beu Support desk, part of the stand-in server (mockAdapter.js).
 *
 * Bailey answers first: a how-to question gets a guide straight away, and
 * anything he can't fix becomes a ticket for the Beu team, raised with the
 * page and device the salon wrote from. Tom from the team then replies in
 * the same chat and marks the ticket fixed, as the approved mockups show.
 *
 * The real backend does the same through Bailey's tools and the support
 * team's admin (see the backend brief); the screens only see the events.
 */
import { localId, firstName } from './model';
import { seedSupport, BEU_PEOPLE, findGuide, isHowTo } from './seedSupport';

export const SUPPORT_ID = 'beu-support';

/** Add Beu Support (and the Beu team) to a store, new or saved. */
export function withSupport(db, me) {
    Object.keys(BEU_PEOPLE).forEach((id) => { db.people[id] = { ...BEU_PEOPLE[id] }; });
    if (db.conversations[SUPPORT_ID]) { db.tickets = db.tickets || []; return db; }
    if (db.fresh) {
        // A new salon: just Bailey saying hello, no history
        const at = new Date().toISOString();
        const conversation = { id: SUPPORT_ID, space: 'support', kind: 'support', name: 'Beu Support', memberIds: [me.id, 'bailey', 'beu-tom'], pinned: true, muted: false, createdAt: at, lastMessageAt: at, readUpTo: { [me.id]: at }, nextTicket: 1001 };
        const hello = { id: 'sf1', conversationId: SUPPORT_ID, authorId: 'bailey', createdAt: at, text: "Hi, I'm Bailey. Ask me anything about Beu and I'll answer straight away. If I can't fix it, the Beu team is one tap away.", attachments: [], reactions: {}, mentions: [] };
        return { ...db, conversations: { ...db.conversations, [SUPPORT_ID]: conversation }, messages: { ...db.messages, [SUPPORT_ID]: [hello] }, tickets: [] };
    }
    const extra = seedSupport(me.id);
    const last = extra.messages.filter((x) => !x.system).slice(-1)[0];
    extra.conversation.lastMessageAt = last ? last.createdAt : extra.conversation.createdAt;
    return {
        ...db,
        conversations: { ...db.conversations, [SUPPORT_ID]: extra.conversation },
        messages: { ...db.messages, [SUPPORT_ID]: extra.messages },
        tickets: extra.tickets,
    };
}

const now = () => new Date().toISOString();
const shorten = (text, n = 48) => (text.length > n ? `${text.slice(0, n - 2).trim()}…` : text);

/**
 * ctx: { db(), me, emit, later, save, append, findMessage, replaceMessage, touch, setConversation }
 * Returns the support calls for the adapter, and simulate(sent) for send().
 */
export function createSupportDesk(ctx) {
    const { me, emit, later } = ctx;
    const line = (text) => ctx.append({ id: localId('srv'), conversationId: SUPPORT_ID, authorId: 'system', createdAt: now(), text: '', attachments: [], reactions: {}, mentions: [], system: { text } });
    const say = (authorId, text, extra = {}) => {
        const m = { id: localId('srv'), conversationId: SUPPORT_ID, authorId, createdAt: now(), text, attachments: [], reactions: {}, mentions: [], ...extra };
        ctx.append(m);
        ctx.touch(SUPPORT_ID, m.createdAt);
        return m;
    };
    /** They read it, type for a moment, then answer. */
    const typeThen = (authorId, wait, fn) => {
        later(Math.max(200, wait - 1400), () => {
            const c = conversation();
            const at = now();
            ctx.setConversation({ ...c, readUpTo: { ...c.readUpTo, [authorId]: at } });
            emit({ type: 'read', conversationId: SUPPORT_ID, userId: authorId, at });
            emit({ type: 'typing', conversationId: SUPPORT_ID, userId: authorId, on: true });
        });
        later(wait, () => { emit({ type: 'typing', conversationId: SUPPORT_ID, userId: authorId, on: false }); fn(); });
    };
    const tickets = () => ctx.db().tickets;
    const emitTickets = () => { ctx.save(); emit({ type: 'tickets', tickets: tickets().map((t) => ({ ...t })) }); };
    const openTicket = () => tickets().find((t) => t.status !== 'fixed') || null;
    const conversation = () => ctx.db().conversations[SUPPORT_ID];
    const setWithTeam = (on) => ctx.setConversation({ ...conversation(), withTeam: on });

    /** Move a ticket on a step, and keep its card in the chat in step. */
    function advance(ref, status, label) {
        const t = tickets().find((x) => x.ref === ref);
        if (!t) return;
        t.status = status;
        t.steps = (t.steps || []).filter((s) => s.id !== status).concat({ id: status, label, at: now() });
        emitTickets();
        const card = t.messageId && ctx.findMessage(SUPPORT_ID, t.messageId);
        if (card && card.card) ctx.replaceMessage({ ...card, card: { ...card.card, status } });
    }

    /** Tom picks a new ticket up, then (in the stand-in) fixes it after your next reply. */
    function teamPicksUp(ticket, urgent) {
        typeThen('beu-tom', urgent ? 4000 : 7000, () => {
            advance(ticket.ref, 'with-team', 'Tom is on it');
            say('beu-tom', urgent
                ? `Hi ${firstName(me.name)}, Tom here. I've got this now and I'm looking at your account. You can carry on taking cash and bank transfers while I do.`
                : `Hi ${firstName(me.name)}, Tom here. Thanks for the detail, I'm looking at this now and will reply here.`);
        });
    }

    function raiseTicket(question, context, { urgent, afterGuide } = {}) {
        const c = conversation();
        const ref = `BEU-${c.nextTicket || 1043}`;
        ctx.setConversation({ ...c, nextTicket: (c.nextTicket || 1043) + 1, withTeam: true });
        const title = urgent ? 'Urgent: payments or diary down' : shorten(question.replace(/[?.!]+$/, ''));
        const msg = say('bailey', urgent
            ? "I've raised this as urgent and alerted the Beu team. Someone will be with you in a few minutes."
            : afterGuide ? "No problem. I've raised a ticket for the team with your question, the page you were on and your device. They'll reply in this chat."
            : "I couldn't fix that from here, so I've raised a ticket for the team with the page you were on and your device. They'll reply in this chat.", {
            card: { type: 'ticket', ref, title, status: 'raised' },
        });
        const ticket = {
            ref, title, status: 'raised', priority: urgent ? 'Urgent' : 'Normal', createdAt: msg.createdAt, messageId: msg.id,
            context: context || [], summary: question, steps: [{ id: 'raised', label: 'Raised by Bailey', at: msg.createdAt }],
        };
        ctx.db().tickets = [ticket].concat(tickets());
        emitTickets();
        teamPicksUp(ticket, urgent);
        return ticket;
    }

    /** The chat is with the team: Tom answers, and fixes the open ticket. */
    function teamReplies() {
        const t = openTicket();
        if (!t || t.status !== 'with-team') return;
        typeThen('beu-tom', 5200, () => {
            say('beu-tom', "Thanks. I've pushed the fix and it's looking stable on our side. I'll mark this as fixed, and if it happens again just reply here.");
            later(1800, () => {
                advance(t.ref, 'fixed', 'Fixed');
                say('beu-tom', '', { card: { type: 'ticket-fixed', ref: t.ref, title: t.title } });
                setWithTeam(!!openTicket());
            });
        });
    }

    /**
     * Hand the chat to the Beu team. From a guide that didn't help: a ticket
     * for that question. From the header: the open ticket if there is one,
     * otherwise a ticket for the last thing asked.
     */
    function talkToTeam(fromMessageId) {
        line("Bailey passed this chat to the Beu team. They'll reply here as soon as they can");
        const open = openTicket();
        if (open && !fromMessageId) {
            setWithTeam(true);
            typeThen('beu-tom', 5000, () => say('beu-tom', `Hi ${firstName(me.name)}, Tom here. What can I help with?`));
            return open;
        }
        const list = ctx.db().messages[SUPPORT_ID] || [];
        const until = fromMessageId ? list.findIndex((x) => x.id === fromMessageId) : list.length;
        const asked = list.slice(0, until === -1 ? list.length : until).filter((x) => x.authorId === me.id && x.text).slice(-1)[0];
        return raiseTicket(asked ? asked.text : 'Asked to talk to the team', asked && asked.context, { afterGuide: !!fromMessageId });
    }

    return {
        /** After send(): Bailey or the team answers. */
        simulate(sent) {
            if (sent.threadRootId) return;
            const c = conversation();
            const text = (sent.text || '').trim();
            const guide = isHowTo(text) ? findGuide(text) : null;
            // With the team, Bailey still answers a how-to he has a guide for.
            if (c.withTeam && openTicket() && !guide) { teamReplies(); return; }
            if (!text) {
                typeThen('bailey', 1600, () => say('bailey', "Thanks for the photo. Tell me what's going wrong and I'll take a look."));
                return;
            }
            typeThen('bailey', 1700, () => {
                if (guide) {
                    say('bailey', guide.answer, { guide: { title: guide.title, sub: guide.sub, cta: guide.cta, href: guide.href }, feedback: true });
                    return;
                }
                raiseTicket(text, sent.context);
            });
        },

        /** "Did that sort it?" Yes: solved by Bailey. No: over to the team. */
        async feedback(messageId, helped) {
            const m = ctx.findMessage(SUPPORT_ID, messageId);
            if (!m) return null;
            ctx.replaceMessage({ ...m, answered: true });
            if (helped) { line('Glad that helped. Marked as solved by Bailey'); return null; }
            return talkToTeam(messageId);
        },

        async talkToTeam() { return talkToTeam(); },

        /** Something urgent: straight to the team, no guides. */
        async urgent(context) {
            const m = say(me.id, "Urgent: I can't take payments or open the diary", { context: context || [], status: 'sent' });
            typeThen('bailey', 1200, () => raiseTicket(m.text, context, { urgent: true }));
            return m;
        },

        /** How did we do? All good closes it; Still broken reopens it. */
        async rate(messageId, rating) {
            const m = ctx.findMessage(SUPPORT_ID, messageId);
            if (!m || !m.card) return null;
            ctx.replaceMessage({ ...m, card: { ...m.card, rated: rating } });
            if (rating === 'good') { line(`Ticket ${m.card.ref} closed`); return null; }
            advance(m.card.ref, 'with-team', 'Reopened, Tom is on it');
            setWithTeam(true);
            line(`Ticket ${m.card.ref} reopened`);
            typeThen('beu-tom', 4500, () => say('beu-tom', "Sorry it's still playing up. I'm taking another look now. What do you see when it happens?"));
            return null;
        },
    };
}
